/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import { withRouter } from 'react-router-dom'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'

import {
  Spinner,
  Table,
  SpanWithData,
  PageNavigationButtons
} from '../'

import {
  PageNavigationControl,
  decodeBase64,
  saveCursor,
  getPageCursor
} from '../../utils'


class RequestRunList extends React.PureComponent {
  static propTypes = {
    identity: PropTypes.string,
    requestType: PropTypes.string,
    origin: PropTypes.string,
    source: PropTypes.string,
    createdAtFrom: PropTypes.string,
    createdAtTo: PropTypes.string,
    updatedAtFrom: PropTypes.string,
    updatedAtTo: PropTypes.string,
    orderBy: PropTypes.string,
    changeSort: PropTypes.func,
    view: PropTypes.oneOf([ 'tabbed', 'list' ]),
    showExtraEmployerInfo: PropTypes.bool,
    defaultResultsPerPage: PropTypes.number,
    onClick: PropTypes.func.isRequired,
    className: PropTypes.string
  }

  static defaultProps = {
    view: 'list',
    defaultResultsPerPage: 15
  }

  state = {
    pageNav: new PageNavigationControl(),
    filterChanged: false,
    resultsPerPage: this.props.defaultResultsPerPage
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if ((this.props.status !== nextProps.status ||
      this.props.employer !== nextProps.employer ||
      this.props.division !== nextProps.division ||
      this.props.requestType !== nextProps.requestType ||
      this.props.origin !== nextProps.origin ||
      this.props.source !== nextProps.source ||
      this.props.createdAtFrom !== nextProps.createdAtFrom ||
      this.props.createdAtTo !== nextProps.createdAtTo ||
      this.props.updatedAtFrom !== nextProps.updatedAtFrom ||
      this.props.updatedAtTo !== nextProps.updatedAtTo ||
      this.props.orderBy !== nextProps.orderBy) &&
      !sessionStorage.getItem('requestRunListPageState')
    ) {
      let previousFilters = JSON.parse(sessionStorage.getItem('requestRunListFilters'))
      if (previousFilters && (
        previousFilters.status === nextProps.status ||
        previousFilters.employer === nextProps.employer ||
        previousFilters.division === nextProps.division ||
        previousFilters.requestType === nextProps.requestType ||
        previousFilters.origin === nextProps.origin ||
        previousFilters.source === nextProps.source ||
        previousFilters.createdAtFrom === nextProps.createdAtFrom ||
        previousFilters.createdAtTo === nextProps.createdAtTo ||
        previousFilters.updatedAtFrom === nextProps.updatedAtFrom ||
        previousFilters.updatedAtTo === nextProps.updatedAtTo ||
        previousFilters.orderBy === nextProps.orderBy
      )) {
        sessionStorage.removeItem('requestRunListFilters')
      } else {
        this.state.pageNav.reset()
        this.setState({
          filterChanged: true
        }, () => this.props.refetch())
        sessionStorage.removeItem('requestRunListFilters')
      }
    } else {
      if (!nextProps.loading) {
        if (this.state.filterChanged) {
          this.setState({
            filterChanged: false
          })
          this.state.pageNav.setPageStates(null, nextProps.requestruns)
        }
        else if (nextProps.requestruns) {
          let requestRunListPageState = JSON.parse(sessionStorage.getItem('requestRunListPageState'))
          if (requestRunListPageState) {
            this.state.pageNav.setPageStates(null, null, requestRunListPageState)
            sessionStorage.removeItem('requestRunListPageState')
            if (requestRunListPageState.resultsPerPage !== this.state.resultsPerPage) {
              this.setState({ resultsPerPage: requestRunListPageState.resultsPerPage })
            }
            this.props.refetch({
              cursor: requestRunListPageState.cursor,
              resultsPerPage: requestRunListPageState.resultsPerPage
            })
          } else if (!Object.is(this.props.requestruns, nextProps.requestruns)) {
            this.state.pageNav.setPageStates(this.props.requestruns, nextProps.requestruns)
          }
        }
      }
    }
  }

  getFiltersFromProps = () => (
    {
      employer: this.props.employer,
      division: this.props.division,
      status: this.props.status,
      requestType: this.props.requestType,
      origin: this.props.origin,
      source: this.props.source,
      createdAtFrom: this.props.createdAtFrom,
      createdAtTo: this.props.createdAtTo,
      updatedAtFrom: this.props.updatedAtFrom,
      updatedAtTo: this.props.updatedAtTo,
      orderBy: this.props.orderBy
    }
  )

  getEmployerDivisionInfo = (identity, showExtraEmployerInfo) => {
    if (identity && identity.isClient) {
      if (showExtraEmployerInfo) {
        return (
          <React.Fragment>
            <div>{identity.client.division.employer.friendlyName}</div>
            {identity.client.division.employer.friendlyName !== identity.client.division.name &&
              <div>{identity.client.division.name}</div>
            }
          </React.Fragment>
        )
      } else {
        return identity.client.division.name
      }
    } else if (identity && identity.isEmployer) {
      return <div>{identity.employer.friendlyName}</div>
    } else {
      return '-'
    }
  }

  changeResultsPerPage = (e) => {
    let resultsPerPage = parseInt(e.target.value, 10)
    this.setState({ resultsPerPage }, () => this.props.refetch({ resultsPerPage, cursor: null }))
  }

  render() {
    if (this.props.loading) {
      return <Spinner type='brand' />
    }

    if (this.props.error) {
      console.log('error occurred', this.props.error)
    }

    let requestRuns = this.props.requestruns
    let tableColumns = this.props.identity
      ? ['Request type', 'Origin', 'Last updated', 'Last Modifier']
      : ['Identity', 'Request type', 'Origin', 'Last updated', 'Last Modifier']

    if (this.props.showExtraEmployerInfo) {
      tableColumns.push('Employer / Division')
    } else {
      tableColumns.push('Division')
    }

    if (this.props.showStatusColumn) {
      tableColumns.unshift('Status')
    }

    if (requestRuns.edges.length === 0) {
      return (
        <div className={this.props.className ? this.props.className : ''}>
          <Table
            columns={tableColumns}
            rows={[
              <tr key='no_records'>
                <td colSpan={6}>
                  No records to display.
                </td>
              </tr>
            ]}
            objectRows={true}
          />
        </div>
      )
    } else {
      let pageNav = this.state.pageNav
      let startCursor = this.props.requestruns.pageInfo.startCursor
      let totalCount = this.props.requestruns.totalCount
      return (
        <div className={this.props.className ? this.props.className : ''}>
          <Table
            columns={tableColumns}
            rows={requestRuns.edges.map(({ node: requestRun }, index) => (
              <tr
                onClick={() => {
                  sessionStorage.setItem('requestRunListPageState', JSON.stringify({
                    firstPage: this.state.pageNav.firstPage,
                    lastPage: this.state.pageNav.lastPage,
                    currentPage: this.state.pageNav.currentPage,
                    pages: this.state.pageNav.pages,
                    cursor: saveCursor(requestRuns.pageInfo.startCursor),
                    resultsPerPage: this.state.resultsPerPage
                  }))
                  sessionStorage.setItem('requestRunListFilters', JSON.stringify({
                    employer: this.props.employer,
                    division: this.props.division,
                    requestType: this.props.requestType,
                    createdAtFrom: this.props.createdAtFrom,
                    createdAtTo: this.props.createdAtTo,
                    updatedAtFrom: this.props.updatedAtFrom,
                    updatedAtTo: this.props.updatedAtTo,
                    status: this.props.status,
                    origin: this.props.origin,
                    source: this.props.source,
                    orderBy: this.props.orderBy
                  }))
                  this.props.onClick(
                    requestRun.identity ? requestRun.identity.id : null,
                    requestRun.id,
                    this.getFiltersFromProps()
                  )
                }}
                className='clickable'
                key={`row_${requestRun.id}`}>
                {this.props.showStatusColumn &&
                  <td>
                    <SpanWithData forField='RequestRunStatus' searchKey={requestRun.status} />
                  </td>
                }
                {!this.props.identity &&
                  <td>
                    {requestRun.identity
                      ? requestRun.identity.fullName
                      : '-'
                    }
                  </td>
                }
                <td>
                  {requestRun.requesttype.name}
                </td>
                <td>
                  {requestRun.origin
                    ? <SpanWithData forField='RequestRunOrigin' searchKey={requestRun.origin} />
                    : '-'
                  }
                </td>
                <td>
                  {moment(requestRun.updatedAt).format('llll')}
                </td>
                <td>
                  {requestRun.updatedBy
                    ? requestRun.updatedBy.identity
                      ? requestRun.updatedBy.identity.fullName
                      : '-'
                    : '-'
                  }
                </td>
                <td>
                  {this.getEmployerDivisionInfo(requestRun.identity)}
                </td>
              </tr>
            ))}
            objectRows={true}
            orderBy={this.props.orderBy}
            changeSort={this.props.changeSort}
            querySortableColumns={{
              'Last updated': {
                orderByName: 'updated_at'
              }
            }}
          />
          <PageNavigationButtons
            onNextClick={() => pageNav.nextPage(this.props.loadNextFiles, this.state.resultsPerPage)}
            onPreviousClick={() => pageNav.previousPage(this.props.loadPreviousFiles, this.state.resultsPerPage)}
            previousDisabled={pageNav.firstPage}
            nextDisabled={pageNav.lastPage}
            startCursor={startCursor}
            totalCount={totalCount}
            resultsPerPage={this.state.resultsPerPage}
            resultsText='requests'
            onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, this.state.resultsPerPage) })}
            onResultsPerPageChange={this.changeResultsPerPage}
            className='slds-p-around_medium'
          />
        </div>
      )
    }
  }
}

const getRequestRuns = gql`
  query($status: String, $requestType: UUID, $origin: String, $source: String, $employer: UUID, $division: UUID, $createdAtFrom: DateTime, $createdAtTo: DateTime, $updatedAtFrom: DateTime, $updatedAtTo: DateTime, $identity: UUID, $assignedTo: UUID, $resultsPerPage: Int, $orderBy: String, $cursor: String ) {
    requestruns(requesttype_Id: $requestType, origin: $origin, requesttype_Source: $source, identity_Client_Division_Employer_Id: $employer, identity_Client_Division_Id: $division, createdAt_Gt: $createdAtFrom, createdAt_Lt: $createdAtTo, updatedAt_Gt: $updatedAtFrom, updatedAt_Lt: $updatedAtTo, status: $status, identity_Id: $identity, assignedTo_Id: $assignedTo, first: $resultsPerPage, after: $cursor, orderBy: $orderBy) {
      totalCount
      edges {
        node {
          id
          createdAt
          updatedAt
          status
          age
          origin
          identity {
            id
            firstName
            lastName
            fullName
            isClient
            isEmployer
            client {
              id
              division {
                id
                name
                employer {
                  id
                  friendlyName
                }
              }
            }
            employer {
              id
              friendlyName
            }
          }
          assignedTo {
            id
            fullName
          }
          updatedBy {
            id
            identity {
              id
              fullName
            }
          }
          requesttype {
            id
            name
          }
          sources {
            id
          }
          workflowrunProcessing {
            id
            status
            atStep
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const getPreviousRequestRuns = gql`
  query($status: String, $requestType: UUID, $origin: String, $source: String, $employer: UUID, $division: UUID, $createdAtFrom: DateTime, $createdAtTo: DateTime, $updatedAtFrom: DateTime, $updatedAtTo: DateTime, $identity: UUID, $assignedTo: UUID, $resultsPerPage: Int, $orderBy: String, $cursor: String ) {
    requestruns(requesttype_Id: $requestType, origin: $origin, requesttype_Source: $source, identity_Client_Division_Employer_Id: $employer, identity_Client_Division_Id: $division, createdAt_Gt: $createdAtFrom, createdAt_Lt: $createdAtTo, updatedAt_Gt: $updatedAtFrom, updatedAt_Lt: $updatedAtTo, status: $status, identity_Id: $identity, assignedTo_Id: $assignedTo, last: $resultsPerPage, before: $cursor, orderBy: $orderBy) {
      totalCount
      edges {
        node {
          id
          createdAt
          updatedAt
          status
          age
          origin
          identity {
            id
            firstName
            lastName
            fullName
            isClient
            isEmployer
            client {
              id
              division {
                id
                name
                employer {
                  id
                  friendlyName
                }
              }
            }
            employer {
              id
              friendlyName
            }
          }
          assignedTo {
            id
            fullName
          }
          updatedBy {
            id
            identity {
              id
              fullName
            }
          }
          requesttype {
            id
            name
          }
          sources {
            id
          }
          workflowrunProcessing {
            id
            status
            atStep
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const withData = graphql(getRequestRuns, {
  options: ({ status, requestType, origin, source, employer, division, createdAtFrom, createdAtTo, updatedAtFrom, updatedAtTo, identity, assignedTo, defaultResultsPerPage = 15, orderBy }) => ({
    variables: {
      status,
      requestType: requestType ? decodeBase64(requestType) : undefined,
      origin,
      source,
      employer: employer ? decodeBase64(employer) : undefined,
      division: division ? decodeBase64(division) : undefined,
      createdAtFrom,
      createdAtTo,
      updatedAtFrom,
      updatedAtTo,
      identity: identity ? decodeBase64(identity) : undefined,
      assignedTo: assignedTo ? decodeBase64(assignedTo) : undefined,
      orderBy,
      resultsPerPage: defaultResultsPerPage
    },
    fetchPolicy: 'network-only'
  }),
  props: ({ ownProps: { status, requestType, origin, source, employer, division, createdAtFrom, createdAtTo, updatedAtFrom, updatedAtTo, identity, assignedTo, orderBy }, data: { loading, error, requestruns, fetchMore, refetch } }) => ({
    loading,
    error,
    requestruns,
    refetch,
    loadNextFiles: (resultsPerPage) => {
      return fetchMore({
        query: getRequestRuns,
        variables: {
          status,
          requestType: requestType ? decodeBase64(requestType) : undefined,
          origin,
          source,
          employer: employer ? decodeBase64(employer) : undefined,
          division: division ? decodeBase64(division) : undefined,
          createdAtFrom,
          createdAtTo,
          updatedAtFrom,
          updatedAtTo,
          identity: identity ? decodeBase64(identity) : undefined,
          assignedTo: assignedTo ? decodeBase64(assignedTo) : undefined,
          orderBy,
          resultsPerPage,
          cursor: requestruns.pageInfo.endCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.requestruns.edges
          const pageInfo = fetchMoreResult.requestruns.pageInfo
          const totalCount = fetchMoreResult.requestruns.totalCount

          return {
            requestruns: {
              __typename: previousResult.requestruns.__typename,
              totalCount,
              edges: [...newEdges],
              pageInfo
            }
          }
        }
      })
    },
    loadPreviousFiles: (resultsPerPage) => {
      return fetchMore({
        query: getPreviousRequestRuns,
        variables: {
          status,
          requestType: requestType ? decodeBase64(requestType) : undefined,
          origin,
          source,
          employer: employer ? decodeBase64(employer) : undefined,
          division: division ? decodeBase64(division) : undefined,
          createdAtFrom,
          createdAtTo,
          updatedAtFrom,
          updatedAtTo,
          identity: identity ? decodeBase64(identity) : undefined,
          assignedTo: assignedTo ? decodeBase64(assignedTo) : undefined,
          orderBy,
          resultsPerPage,
          cursor: requestruns.pageInfo.startCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.requestruns.edges
          const pageInfo = fetchMoreResult.requestruns.pageInfo
          const totalCount = fetchMoreResult.requestruns.totalCount

          return {
            requestruns: {
              __typename: previousResult.requestruns.__typename,
              totalCount,
              edges: [...newEdges],
              pageInfo
            }
          }
        }
      })
    }
  })
})

export default compose(
  withData,
  withRouter
)(RequestRunList)



// WEBPACK FOOTER //
// ./src/components/request-run-list/index.js